/**
 * THE WORDS A COLUMN AND A CARD SAY about the same two gaps, who owns an enquiry and what happens
 * next, written once so the table and the card can never disagree. And where a booked one ends.
 */
import { Link } from 'react-router-dom';
import type { EnquiryRow, Me } from '../../api.ts';
import { shortDay } from '../../format.ts';
import { openEnquiry } from '../../source.ts';

/**
 * WHO HAS THIS, BY NAME. The list used to carry the owner's id and nothing else, so a screen could
 * name nobody but the person signed in and everybody else was "Somebody else"; `listEnquiries`
 * joins `users.display_name` onto the row now and that word is gone.
 *
 * YOU IS STILL "YOU" and not your own name, because "Victoria" beside eleven rows tells a person
 * reading her own screen nothing that "You" does not tell her faster. The join is what makes the
 * OTHER rows readable. A row whose owner has no name at all keeps the old word rather than a blank.
 *
 * Nobody is coral only while the enquiry is still open: an unowned booked one is a settled fact
 * and not a gap.
 */
function ownerWords(row: EnquiryRow, me: Me): string {
  if (row.ownerUserId === me.userId) return 'You';
  return row.ownerName ?? 'Somebody else';
}

export function OwnerWord({ row, me }: { row: EnquiryRow; me: Me }) {
  if (row.ownerUserId === null) {
    return openEnquiry(row.state) ? (
      <span className="font-medium text-coral">Nobody</span>
    ) : (
      <span className="text-navy/50">Nobody</span>
    );
  }
  // Wrapped rather than cut: a first and last name is wider than the column (ops #56).
  return <span className="break-words">{ownerWords(row, me)}</span>;
}

export function NextActionWords({ row, today }: { row: EnquiryRow; today: string }) {
  const open = openEnquiry(row.state);
  if (row.nextAction === null && row.nextActionOn === null) {
    return open ? (
      <span className="font-medium text-coral">No next action yet</span>
    ) : (
      <span className="text-navy/50">None</span>
    );
  }
  const overdue = open && row.nextActionOn !== null && row.nextActionOn < today;
  const dayless = open && row.nextActionOn === null;
  const when =
    row.nextActionOn === null
      ? 'no day set'
      : overdue
        ? `due ${shortDay(row.nextActionOn)}`
        : shortDay(row.nextActionOn);
  return (
    <span className={overdue || dayless ? 'font-medium text-coral' : ''}>
      {row.nextAction ?? 'Follow up'}, {when}
    </span>
  );
}

/**
 * WHERE A BOOKED ENQUIRY ENDS, which until now was a sentence and a hunt through the client book.
 *
 * `listEnquiries` reads the household off `clients.enquiry_id` as one value per enquiry, so this is
 * a link to her record rather than an instruction to go and look for it. A booked row with no
 * household behind it is a real answer too, and it says so: the record was thrown away afterwards,
 * and sending anybody to a page that is not there would be worse than the old sentence.
 *
 * AND A BOOKING CANCELLED BEFORE ANY CLEAN SAYS SO (ops #47, step 4): her household ended with none
 * finished, so this row moved to `lost` and is still where that household came from.
 */
export function SheIsAHousehold({ row }: { row: EnquiryRow }) {
  const cancelled = row.state === 'lost';
  if (!row.clientId) {
    return (
      <p className="text-sm text-navy/70">
        {cancelled
          ? 'Booked, then cancelled before a clean, and that household is no longer in the book.'
          : 'A client once, and that household is no longer in the book.'}
      </p>
    );
  }
  return (
    <p className="text-sm text-navy/70">
      {cancelled ? 'Booked, then cancelled before a clean.' : 'A client now.'}{' '}
      <Link to={`/clients/${row.clientId}`} className="font-medium text-navy underline">
        Open their household
      </Link>
      .
    </p>
  );
}

/** The same two facts as sentences, for a card and for the panel. */
export function Owner({ row, me, open }: { row: EnquiryRow; me: Me; open: boolean }) {
  if (row.ownerUserId === null) {
    return open ? <p className="font-medium text-coral">Nobody owns this.</p> : null;
  }
  if (row.ownerUserId === me.userId) return <p>You own this.</p>;
  return <p>{row.ownerName ?? 'Somebody else'} owns this.</p>;
}

export function NextAction({
  row,
  open,
  overdue,
}: {
  row: EnquiryRow;
  open: boolean;
  overdue: boolean;
}) {
  if (row.nextAction === null && row.nextActionOn === null) {
    return open ? <p className="font-medium text-coral">No next action yet.</p> : null;
  }
  const dayless = open && row.nextActionOn === null;
  const when =
    row.nextActionOn === null
      ? 'no day set'
      : overdue
        ? `was due ${shortDay(row.nextActionOn)}`
        : `by ${shortDay(row.nextActionOn)}`;
  return (
    <p className={overdue || dayless ? 'font-medium text-coral' : ''}>
      Next: {row.nextAction ?? 'follow up'}, {when}.
    </p>
  );
}
